/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { useNavigate } from 'react-router-dom';
import { Pathname } from 'Routes';
import AlterImage from 'Assets/no-image-available.png';
import { getThumbnailTileStyle } from 'Config/featureFlags';
import PlayerControls from './playerControls';
import styles from './index.module.css';
import { TitleLabel } from './TitleLabel';

const VideoAutoPlayer = ({ dimensions, videoDetails, videoID, autoPlay, thumbNail, isLocked, onLockedClick, onOpen }) => {
  const playerRef = useRef(null);
  const isTrueValue = true;
  const [state, setState] = useState({
    playing: true,
    played: 0,
    seeking: false,
    buffering: true,
    muted: true,
  });
  const navigate = useNavigate();

  const { playing, played, seeking, buffering, muted } = state;

  const format = (seconds) => {
    if (Number.isNaN(seconds)) {
      return '00:00';
    }
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, '0');
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const currentTime = playerRef.current
    ? playerRef.current.getCurrentTime()
    : '00:00';
  const duration = playerRef.current
    ? playerRef.current.getDuration()
    : '00:00';
  const elapsedTime = playerRef.current ? format(currentTime) : '00:00';
  const totalDuration = playerRef.current ? format(duration) : '00:00';

  useEffect(() => {
    if (elapsedTime === '0:20') {
      setTimeout(() => {
        setState({ ...state, playing: false, buffering: true });
      }, 500);
    }
  }, [elapsedTime]);

  const onEndBuffering = () => {
    setState({ ...state, buffering: false });
  };

  const onEndedHandler = () => {
    setState({ ...state, buffering: true });
  };

  const handleSeekChange = (newValue) => {
    setState({ ...state, played: Math.floor(newValue) });
  };

  const handleSeekMouseUp = (newValue) => {
    setState({ ...state, seeking: false });
    playerRef.current.seekTo(newValue);
  };

  const handleProgress = (changeState) => {
    if (!seeking) {
      setState({ ...state, ...changeState });
    }
  };
  const [isLoading, setIsLoading] = useState(true);
  // Portrait sources (naturally taller than wide) would otherwise get
  // cropped hard by object-fit: cover in this landscape box. Instead they
  // switch to object-fit: contain plus a blurred, scaled-up copy of the same
  // image filling the space behind them — full thumbnail visible, no crop,
  // no distortion, no flat black bars.
  const [isPortrait, setIsPortrait] = useState(false);
  // Measured thumbnail aspect ratio (w/h) — drives the adaptive tile's width at
  // a fixed height. null until the image reports its natural size.
  const [aspect, setAspect] = useState(null);
  const thumbImgRef = useRef(null);

  // If the thumbnail is already in the browser cache by the time React
  // attaches the <img>, the `load` event may have fired before the listener
  // was bound — leaving isLoading stuck on true and the shimmer visible
  // forever. Sync state explicitly when the element is already complete.
  useEffect(() => {
    const img = thumbImgRef.current;
    if (img && img.complete && img.naturalHeight > 0) {
      setIsLoading(false);
      setIsPortrait(img.naturalHeight > img.naturalWidth);
      setAspect(img.naturalWidth / img.naturalHeight);
    }
  }, [thumbNail]);

  const handleClick = (e) => {
    if (isLocked && onLockedClick) {
      e.preventDefault();
      e.stopPropagation();
      onLockedClick();
    } else if (onOpen) {
      // Open in place (Netflix modal on the same page) instead of navigating.
      // Fall back to videoID (what the original navigate used) if the video
      // object has no id, so the modal always gets a valid id.
      e.preventDefault();
      e.stopPropagation();
      onOpen(videoDetails?.id || videoID);
    } else {
      navigate(Pathname.getVideoPath(videoID));
    }
  };

  // Tile display style — read from localStorage at render (Preferences modal).
  //   letterbox → fixed 16:9; portrait shown contain + blurred backdrop.
  //   adaptive  → fixed row height, width follows the thumbnail aspect, cover.
  //   poster    → uniform 2:3 portrait poster, cover-cropped.
  //   framed    → small contain thumbnail with title/metadata beside it.
  //   cinematic → adaptive-sized tile with the title overlaid on a gradient.
  const tileStyle = getThumbnailTileStyle();
  const ADAPTIVE_TILE = tileStyle === 'adaptive';
  const POSTER_TILE = tileStyle === 'poster';
  const FRAMED_TILE = tileStyle === 'framed';
  const CINEMATIC_TILE = tileStyle === 'cinematic';
  // Fixed-height + measured-aspect width sizing (adaptive and cinematic).
  const ASPECT_SIZED = ADAPTIVE_TILE || CINEMATIC_TILE;
  // Title drawn on the thumbnail (with a gradient scrim) instead of below it.
  const OVERLAY_TITLE = CINEMATIC_TILE;

  // Per-style tile box: extra class + (aspect-sized) inline aspect ratio.
  const containerClass = [
    styles.videoPlayer,
    ASPECT_SIZED && styles.videoPlayerAdaptive,
    POSTER_TILE && styles.videoPlayerPoster,
    FRAMED_TILE && styles.videoPlayerFramed,
    CINEMATIC_TILE && styles.videoPlayerCinematic,
  ].filter(Boolean).join(' ');
  const containerStyle = ASPECT_SIZED ? { aspectRatio: aspect || 16 / 9 } : undefined;
  // Show the whole frame (object-fit: contain): always for framed, and for
  // letterbox portraits. adaptive/poster/cinematic cover-fill.
  const thumbContain = FRAMED_TILE
    || (!ADAPTIVE_TILE && !POSTER_TILE && !CINEMATIC_TILE && isPortrait);
  // Blurred backdrop only fills the letterbox portrait's empty sides.
  const showBackdrop = tileStyle === 'letterbox' && isPortrait;

  return (
    <div
      onClick={handleClick}
      id="autoplay-div"
      className={containerClass}
      style={containerStyle}
    >
      {autoPlay ? (
        <ReactPlayer
          ref={playerRef}
          width="100%"
          height="200px"
          url={videoDetails?.video_file}
          className={styles.react_player}
          playing={playing}
          muted={muted}
          onBufferEnd={onEndBuffering}
          onProgress={handleProgress}
          autoPlay={autoPlay}
          progressInterval={100}
          onEnded={onEndedHandler}
        />
      )
        : (
<div className={styles.thumbWrap}>
  {/* Letterbox portrait only: blurred backdrop fills the empty sides behind the
      contained frame. adaptive/poster crop-fill; framed uses a solid bg. */}
  {showBackdrop && (
    <img
      className={styles.thumbBackdrop}
      src={thumbNail}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
    />
  )}
  <img
    ref={thumbImgRef}
    className={thumbContain ? `${styles.thumbNail} ${styles.thumbNailContain}` : styles.thumbNail}
    src={thumbNail}
    alt="thumbnail"
    width="100%"
    height="200px"
    loading="lazy"
    decoding="async"
    style={{ opacity: isLoading ? 0 : 1 }}
    onLoad={(e) => {
      setIsLoading(false);
      setIsPortrait(e.target.naturalHeight > e.target.naturalWidth);
      if (e.target.naturalWidth && e.target.naturalHeight) {
        setAspect(e.target.naturalWidth / e.target.naturalHeight);
      }
    }}
    onError={(e) => {
      e.target.onerror = null;
      e.target.src = AlterImage;
      setIsLoading(false);
    }}
  />
  {isLoading && (
    <div className={styles.ShimmerLoader} />
  )}
  {/* cinematic: title drawn on the thumbnail over a bottom gradient
      (the separate TitleLabel below is suppressed in this style). */}
  {OVERLAY_TITLE && videoDetails?.title && (
    <div className={styles.tileOverlayTitle}>
      <span>{videoDetails.title}</span>
    </div>
  )}
</div>
)}
    </div>
  );
};
export default VideoAutoPlayer;
